import React, { useState } from "react";
import {
  Chip,
  Popover,
  Box,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Button,
  Typography,
} from "@mui/material";
import { CalendarToday, KeyboardArrowDown, Close } from "@mui/icons-material";
import { transparentColors } from "../../utils/theme";
import {
  format,
  isToday,
  isYesterday,
  subDays,
  startOfMonth,
  endOfMonth,
  subMonths,
} from "date-fns";
import { useAppDispatch, useAppSelector } from "../../store/store";
import {
  setSingleDate,
  setDateRange,
  setToday,
  setYesterday,
  setLast7Days,
} from "../../store/slices/dateFilterSlice";

interface CompactDateFilterProps {
  /** View context for contextual date display (daily/monthly/yearly) */
  viewContext?: "daily" | "monthly" | "yearly";
  /** Callback to change view when preset is selected */
  onViewChange?: (view: "daily" | "monthly" | "yearly") => void;
}

/**
 * CompactDateFilter - A minimal, space-efficient date filter component
 *
 * Displays current filter as a chip/badge that opens a popover with full picker
 * Perfect for toolbar placement without consuming vertical space
 */
const CompactDateFilter: React.FC<CompactDateFilterProps> = ({
  viewContext,
  onViewChange,
}) => {
  const dispatch = useAppDispatch();
  const { mode, selectedDate, dateRange } = useAppSelector(
    (state) => state.dateFilter,
  );

  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [tempMode, setTempMode] = useState(mode);
  const [tempStartDate, setTempStartDate] = useState<string>(
    dateRange?.start
      ? format(dateRange.start, "yyyy-MM-dd")
      : format(new Date(), "yyyy-MM-dd"),
  );
  const [tempEndDate, setTempEndDate] = useState<string>(
    dateRange?.end
      ? format(dateRange.end, "yyyy-MM-dd")
      : format(new Date(), "yyyy-MM-dd"),
  );
  const [tempSingleDate, setTempSingleDate] = useState<string>(
    format(selectedDate, "yyyy-MM-dd"),
  );

  const open = Boolean(anchorEl);
  const today = new Date();
  const maxDate = format(today, "yyyy-MM-dd");

  // Generate display label for the chip
  const getDisplayLabel = () => {
    // If user has manually selected a date range (not a default preset), show the custom range
    const isCustomRange = mode === "range" && dateRange && !isCommonPreset();

    if (isCustomRange) {
      return `${format(dateRange.start, "MMM dd")} - ${format(
        dateRange.end,
        "MMM dd, yyyy",
      )}`;
    }

    // Show contextual labels based on viewContext (monthly/yearly tabs)
    if (viewContext && mode === "single" && isToday(selectedDate)) {
      if (viewContext === "monthly") return "This Month";
      if (viewContext === "yearly") return "This Year";
    }

    // Default labels for single date mode
    if (mode === "single") {
      if (isToday(selectedDate)) return "Today";
      if (isYesterday(selectedDate)) return "Yesterday";
      return format(selectedDate, "MMM dd, yyyy");
    } else if (dateRange) {
      const daysDiff = Math.ceil(
        (dateRange.end.getTime() - dateRange.start.getTime()) /
          (1000 * 60 * 60 * 24),
      );

      // Check for common presets
      if (daysDiff === 6 && isToday(dateRange.end)) return "Last 7 Days";
      if (daysDiff === 29 && isToday(dateRange.end)) return "Last 30 Days";

      // Check if it's current month
      const currentMonthStart = startOfMonth(today);
      const currentMonthEnd = endOfMonth(today);
      if (
        format(dateRange.start, "yyyy-MM-dd") ===
          format(currentMonthStart, "yyyy-MM-dd") &&
        format(dateRange.end, "yyyy-MM-dd") ===
          format(currentMonthEnd, "yyyy-MM-dd")
      ) {
        return "This Month";
      }

      return `${format(dateRange.start, "MMM dd")} - ${format(
        dateRange.end,
        "MMM dd, yyyy",
      )}`;
    }
    return "Select Date";
  };

  // Helper to check if current selection is a common preset
  const isCommonPreset = () => {
    if (!dateRange) return false;

    const daysDiff = Math.ceil(
      (dateRange.end.getTime() - dateRange.start.getTime()) /
        (1000 * 60 * 60 * 24),
    );

    // Check for common presets
    if (daysDiff === 6 && isToday(dateRange.end)) return true;
    if (daysDiff === 29 && isToday(dateRange.end)) return true;

    // Check if it's current month
    const currentMonthStart = startOfMonth(today);
    const currentMonthEnd = endOfMonth(today);
    if (
      format(dateRange.start, "yyyy-MM-dd") ===
        format(currentMonthStart, "yyyy-MM-dd") &&
      format(dateRange.end, "yyyy-MM-dd") ===
        format(currentMonthEnd, "yyyy-MM-dd")
    ) {
      return true;
    }

    return false;
  };

  // Check if filter is in default state (Today)
  const isDefaultState = mode === "single" && isToday(selectedDate);

  const handleOpen = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
    // Initialize temp values from current state
    setTempMode(mode);
    setTempSingleDate(format(selectedDate, "yyyy-MM-dd"));
    if (dateRange) {
      setTempStartDate(format(dateRange.start, "yyyy-MM-dd"));
      setTempEndDate(format(dateRange.end, "yyyy-MM-dd"));
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClear = () => {
    dispatch(setToday());
  };

  const handleApply = () => {
    if (tempMode === "single") {
      dispatch(setSingleDate(new Date(tempSingleDate)));
    } else {
      const start = new Date(tempStartDate);
      const end = new Date(tempEndDate);
      if (start <= end) {
        dispatch(setDateRange({ start, end }));
      }
    }
    handleClose();
  };

  const handlePreset = (preset: string) => {
    switch (preset) {
      case "today":
        dispatch(setToday());
        onViewChange?.("daily");
        break;
      case "yesterday":
        dispatch(setYesterday());
        onViewChange?.("daily");
        break;
      case "last7":
        dispatch(setLast7Days());
        onViewChange?.("daily");
        break;
      case "last30":
        const thirtyDaysAgo = subDays(today, 29);
        dispatch(setDateRange({ start: thirtyDaysAgo, end: today }));
        onViewChange?.("daily");
        break;
      case "thisMonth":
        dispatch(
          setDateRange({
            start: startOfMonth(today),
            end: endOfMonth(today),
          }),
        );
        onViewChange?.("monthly");
        break;
      case "lastMonth":
        const lastMonthStart = startOfMonth(subMonths(today, 1));
        const lastMonthEnd = endOfMonth(subMonths(today, 1));
        dispatch(setDateRange({ start: lastMonthStart, end: lastMonthEnd }));
        onViewChange?.("monthly");
        break;
      case "thisYear":
        const yearStart = new Date(today.getFullYear(), 0, 1);
        const yearEnd = new Date(today.getFullYear(), 11, 31);
        dispatch(setDateRange({ start: yearStart, end: yearEnd }));
        onViewChange?.("yearly");
        break;
      case "lastYear":
        const lastYearStart = new Date(today.getFullYear() - 1, 0, 1);
        const lastYearEnd = new Date(today.getFullYear() - 1, 11, 31);
        dispatch(setDateRange({ start: lastYearStart, end: lastYearEnd }));
        onViewChange?.("yearly");
        break;
    }
    handleClose();
  };

  return (
    <>
      <Stack direction="row" spacing={1.5} alignItems="center">
        {/* Select Date Button */}
        <Chip
          icon={<CalendarToday fontSize="small" />}
          label="Select Date"
          onClick={handleOpen}
          deleteIcon={<KeyboardArrowDown />}
          onDelete={handleOpen}
          variant="outlined"
          sx={{
            height: 38,
            fontSize: "0.875rem",
            fontWeight: 600,
            color: "white",
            borderColor: transparentColors.white60,
            borderWidth: "1.5px",
            px: 0.5,
            "& .MuiChip-icon": {
              color: "white",
              fontSize: "1.1rem",
            },
            "& .MuiChip-deleteIcon": {
              fontSize: "1.25rem",
              color: transparentColors.white90,
            },
            "& .MuiChip-label": {
              px: 1,
            },
            cursor: "pointer",
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              backgroundColor: transparentColors.white15,
              borderColor: "white",
              transform: "translateY(-1px)",
              boxShadow: `0 4px 12px ${transparentColors.black15}`,
            },
          }}
        />

        {/* Current Selection Display */}
        <Chip
          label={getDisplayLabel()}
          onDelete={isDefaultState ? undefined : handleClear}
          deleteIcon={<Close fontSize="small" />}
          variant="filled"
          sx={{
            height: 34,
            fontSize: "0.875rem",
            backgroundColor: transparentColors.white25,
            backdropFilter: "blur(10px)",
            color: "white",
            fontWeight: 600,
            letterSpacing: "0.3px",
            px: 0.5,
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            "& .MuiChip-deleteIcon": {
              color: "rgba(255,255,255,0.9)",
              fontSize: "1.1rem",
              transition: "all 0.2s ease",
              "&:hover": {
                color: "white",
                transform: "scale(1.1)",
              },
            },
            "&:hover": {
              backgroundColor: transparentColors.white30,
              transform: "translateY(-1px)",
              boxShadow: `0 4px 12px ${transparentColors.black15}`,
            },
            ...(isDefaultState && {
              backgroundColor: transparentColors.white15,
              fontWeight: 500,
            }),
          }}
        />
      </Stack>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        slotProps={{
          paper: {
            elevation: 12,
            sx: {
              mt: 1.5,
              borderRadius: 3,
              overflow: "visible",
              background:
                "linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0.95) 100%)",
              backdropFilter: "blur(20px)",
              boxShadow: `0 12px 48px ${transparentColors.black15}, 0 0 0 1px ${transparentColors.black05}`,
              "&::before": {
                content: '""',
                position: "absolute",
                top: -8,
                left: 20,
                width: 16,
                height: 16,
                bgcolor: "background.paper",
                transform: "rotate(45deg)",
                boxShadow: `-3px -3px 6px ${transparentColors.black05}`,
              },
            },
          },
        }}
      >
        <Box sx={{ p: 3, minWidth: 380, maxWidth: 420 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              mb: 2.5,
              pb: 2,
              borderBottom: "2px solid",
              borderColor: "divider",
            }}
          >
            <CalendarToday sx={{ color: "primary.main", fontSize: "1.5rem" }} />
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{
                background: "linear-gradient(135deg, #1a237e 0%, #d4af37 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Select Date Range
            </Typography>
          </Box>

          {/* Quick Presets */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="caption"
              fontWeight={600}
              color="text.secondary"
              sx={{
                mb: 1.5,
                display: "block",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Quick Select
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                alignItems: "center",
              }}
            >
              <Chip
                label="Today"
                size="small"
                onClick={() => handlePreset("today")}
                variant={
                  tempMode === "single" &&
                  tempSingleDate === format(today, "yyyy-MM-dd") &&
                  viewContext === "daily"
                    ? "filled"
                    : "outlined"
                }
                sx={{
                  fontWeight: 600,
                  borderRadius: 2,
                  transition: "all 0.2s ease",
                  ...(tempMode === "single" &&
                  tempSingleDate === format(today, "yyyy-MM-dd") &&
                  viewContext === "daily"
                    ? {
                        background:
                          "linear-gradient(135deg, #1a237e 0%, #3949ab 100%)",
                        color: "white",
                        borderColor: "transparent",
                        boxShadow: "0 4px 12px rgba(26, 35, 126, 0.3)",
                      }
                    : {
                        borderColor: "rgba(0,0,0,0.12)",
                        "&:hover": {
                          backgroundColor: "rgba(26, 35, 126, 0.08)",
                          borderColor: "primary.main",
                          transform: "translateY(-1px)",
                          boxShadow: "0 2px 8px rgba(26, 35, 126, 0.15)",
                        },
                      }),
                }}
              />
              <Chip
                label="Yesterday"
                size="small"
                onClick={() => handlePreset("yesterday")}
                variant="outlined"
                sx={{
                  fontWeight: 600,
                  borderRadius: 2,
                  borderColor: "rgba(0,0,0,0.12)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "rgba(26, 35, 126, 0.08)",
                    borderColor: "primary.main",
                    transform: "translateY(-1px)",
                    boxShadow: "0 2px 8px rgba(26, 35, 126, 0.15)",
                  },
                }}
              />
              <Chip
                label="Last 7 Days"
                size="small"
                onClick={() => handlePreset("last7")}
                variant="outlined"
                sx={{
                  fontWeight: 600,
                  borderRadius: 2,
                  borderColor: "rgba(0,0,0,0.12)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "rgba(26, 35, 126, 0.08)",
                    borderColor: "primary.main",
                    transform: "translateY(-1px)",
                    boxShadow: "0 2px 8px rgba(26, 35, 126, 0.15)",
                  },
                }}
              />
              <Chip
                label="Last 30 Days"
                size="small"
                onClick={() => handlePreset("last30")}
                variant="outlined"
                sx={{
                  fontWeight: 600,
                  borderRadius: 2,
                  borderColor: "rgba(0,0,0,0.12)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "rgba(26, 35, 126, 0.08)",
                    borderColor: "primary.main",
                    transform: "translateY(-1px)",
                    boxShadow: "0 2px 8px rgba(26, 35, 126, 0.15)",
                  },
                }}
              />
              <Chip
                label="This Month"
                size="small"
                onClick={() => handlePreset("thisMonth")}
                variant={
                  tempMode === "range" &&
                  dateRange &&
                  format(dateRange.start, "yyyy-MM-dd") ===
                    format(startOfMonth(today), "yyyy-MM-dd") &&
                  format(dateRange.end, "yyyy-MM-dd") ===
                    format(endOfMonth(today), "yyyy-MM-dd") &&
                  viewContext === "monthly"
                    ? "filled"
                    : "outlined"
                }
                sx={{
                  fontWeight: 600,
                  borderRadius: 2,
                  transition: "all 0.2s ease",
                  ...(tempMode === "range" &&
                  dateRange &&
                  format(dateRange.start, "yyyy-MM-dd") ===
                    format(startOfMonth(today), "yyyy-MM-dd") &&
                  format(dateRange.end, "yyyy-MM-dd") ===
                    format(endOfMonth(today), "yyyy-MM-dd") &&
                  viewContext === "monthly"
                    ? {
                        background:
                          "linear-gradient(135deg, #1a237e 0%, #3949ab 100%)",
                        color: "white",
                        borderColor: "transparent",
                        boxShadow: "0 4px 12px rgba(26, 35, 126, 0.3)",
                      }
                    : {
                        borderColor: "rgba(0,0,0,0.12)",
                        "&:hover": {
                          backgroundColor: "rgba(26, 35, 126, 0.08)",
                          borderColor: "primary.main",
                          transform: "translateY(-1px)",
                          boxShadow: "0 2px 8px rgba(26, 35, 126, 0.15)",
                        },
                      }),
                }}
              />
              <Chip
                label="Last Month"
                size="small"
                onClick={() => handlePreset("lastMonth")}
                variant="outlined"
                sx={{
                  fontWeight: 600,
                  borderRadius: 2,
                  borderColor: "rgba(0,0,0,0.12)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "rgba(26, 35, 126, 0.08)",
                    borderColor: "primary.main",
                    transform: "translateY(-1px)",
                    boxShadow: "0 2px 8px rgba(26, 35, 126, 0.15)",
                  },
                }}
              />
              <Chip
                label="This Year"
                size="small"
                onClick={() => handlePreset("thisYear")}
                variant={
                  tempMode === "range" &&
                  dateRange &&
                  format(dateRange.start, "yyyy-MM-dd") ===
                    format(new Date(today.getFullYear(), 0, 1), "yyyy-MM-dd") &&
                  format(dateRange.end, "yyyy-MM-dd") ===
                    format(
                      new Date(today.getFullYear(), 11, 31),
                      "yyyy-MM-dd",
                    ) &&
                  viewContext === "yearly"
                    ? "filled"
                    : "outlined"
                }
                sx={{
                  fontWeight: 600,
                  borderRadius: 2,
                  transition: "all 0.2s ease",
                  ...(tempMode === "range" &&
                  dateRange &&
                  format(dateRange.start, "yyyy-MM-dd") ===
                    format(new Date(today.getFullYear(), 0, 1), "yyyy-MM-dd") &&
                  format(dateRange.end, "yyyy-MM-dd") ===
                    format(
                      new Date(today.getFullYear(), 11, 31),
                      "yyyy-MM-dd",
                    ) &&
                  viewContext === "yearly"
                    ? {
                        background:
                          "linear-gradient(135deg, #1a237e 0%, #3949ab 100%)",
                        color: "white",
                        borderColor: "transparent",
                        boxShadow: "0 4px 12px rgba(26, 35, 126, 0.3)",
                      }
                    : {
                        borderColor: "rgba(0,0,0,0.12)",
                        "&:hover": {
                          backgroundColor: "rgba(26, 35, 126, 0.08)",
                          borderColor: "primary.main",
                          transform: "translateY(-1px)",
                          boxShadow: "0 2px 8px rgba(26, 35, 126, 0.15)",
                        },
                      }),
                }}
              />
              <Chip
                label="Last Year"
                size="small"
                onClick={() => handlePreset("lastYear")}
                variant="outlined"
                sx={{
                  fontWeight: 600,
                  borderRadius: 2,
                  borderColor: "rgba(0,0,0,0.12)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "rgba(26, 35, 126, 0.08)",
                    borderColor: "primary.main",
                    transform: "translateY(-1px)",
                    boxShadow: "0 2px 8px rgba(26, 35, 126, 0.15)",
                  },
                }}
              />
            </Box>
          </Box>

          {/* Mode Toggle */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="caption"
              fontWeight={600}
              color="text.secondary"
              sx={{
                mb: 1.5,
                display: "block",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Filter Mode
            </Typography>
            <ToggleButtonGroup
              value={tempMode}
              exclusive
              onChange={(_, value) => value && setTempMode(value)}
              size="small"
              fullWidth
              sx={{
                "& .MuiToggleButton-root": {
                  fontWeight: 600,
                  py: 1,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "rgba(26, 35, 126, 0.08)",
                  },
                  "&.Mui-selected": {
                    background:
                      "linear-gradient(135deg, #1a237e 0%, #3949ab 100%)",
                    color: "white",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #0d1642 0%, #283593 100%)",
                    },
                  },
                },
              }}
            >
              <ToggleButton value="single">Single Day</ToggleButton>
              <ToggleButton value="range">Date Range</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Date Inputs - Always visible based on mode */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="caption"
              fontWeight={600}
              color="text.secondary"
              sx={{
                mb: 1.5,
                display: "block",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              {tempMode === "single" ? "Select Date" : "Date Range"}
            </Typography>
            {tempMode === "single" ? (
              <TextField
                type="date"
                value={tempSingleDate}
                onChange={(e) => setTempSingleDate(e.target.value)}
                fullWidth
                size="medium"
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: maxDate }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "primary.main",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "primary.main",
                      borderWidth: 2,
                    },
                  },
                }}
              />
            ) : (
              <Stack spacing={2}>
                <TextField
                  type="date"
                  label="Start Date"
                  value={tempStartDate}
                  onChange={(e) => setTempStartDate(e.target.value)}
                  fullWidth
                  size="medium"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ max: maxDate }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "primary.main",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "primary.main",
                        borderWidth: 2,
                      },
                    },
                  }}
                />
                <TextField
                  type="date"
                  label="End Date"
                  value={tempEndDate}
                  onChange={(e) => setTempEndDate(e.target.value)}
                  fullWidth
                  size="medium"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ max: maxDate }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "primary.main",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "primary.main",
                        borderWidth: 2,
                      },
                    },
                  }}
                />
              </Stack>
            )}
          </Box>

          {/* Actions */}
          <Stack
            direction="row"
            spacing={1.5}
            justifyContent="flex-end"
            sx={{
              mt: 3,
              pt: 2.5,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <Button
              onClick={handleClose}
              size="medium"
              sx={{
                fontWeight: 600,
                px: 3,
                borderRadius: 2,
                textTransform: "none",
                color: "text.secondary",
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.04)",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleApply}
              variant="contained"
              size="medium"
              sx={{
                fontWeight: 600,
                px: 3,
                borderRadius: 2,
                textTransform: "none",
                background: "linear-gradient(135deg, #1a237e 0%, #3949ab 100%)",
                boxShadow: "0 4px 12px rgba(26, 35, 126, 0.3)",
                transition: "all 0.2s ease",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #0d1642 0%, #283593 100%)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 20px rgba(26, 35, 126, 0.4)",
                },
              }}
            >
              Apply Filter
            </Button>
          </Stack>
        </Box>
      </Popover>
    </>
  );
};

export default CompactDateFilter;
